import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, Subject, Subscription, BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, skip, startWith, takeUntil } from 'rxjs/operators';
import { ConfiguratorContext, CONFIGURATOR_CONTEXT } from '../configurator-context';

/**
 * Base class for configurator services with instance lifecycle management.
 *
 * This class provides automatic lifecycle management for services that
 * need to maintain state per configurator instance. It handles:
 *
 * 1. **Automatic state reset** - When the instance changes, internal state is reset
 * 2. **Subscription management** - Subscriptions are automatically cleaned up
 * 3. **Instance-aware distinctUntilChanged** - Comparisons restart on instance change
 * 4. **Lifecycle hooks** - `onInstanceInit()` and `onInstanceDestroy()` for custom logic
 *
 * ## Usage
 *
 * ```typescript
 * @Injectable()
 * export class MyService extends ConfiguratorService {
 *     private selectedItem: Item | null = null;
 *
 *     constructor(
 *         @Optional() @Inject(CONFIGURATOR_CONTEXT) context: ConfiguratorContext | null,
 *         private facade: MyFacade
 *     ) {
 *         super(context);
 *         this.initSubscriptions();
 *     }
 *
 *     protected initSubscriptions(): void {
 *         this.addSubscription(
 *             this.facade.selectedItem$.pipe(
 *                 this.instanceAwareDistinct((a, b) => a?.id === b?.id)
 *             ).subscribe(item => {
 *                 this.selectedItem = item;
 *                 this.doSomething();
 *             })
 *         );
 *     }
 *
 *     protected resetState(): void {
 *         this.selectedItem = null;
 *     }
 *
 *     protected onInstanceInit(): void {
 *         // Called when a new instance starts
 *     }
 *
 *     protected onInstanceDestroy(): void {
 *         // Called when the current instance is destroyed
 *     }
 * }
 * ```
 */
@Injectable()
export abstract class ConfiguratorService implements OnDestroy {
    /**
     * Subject that emits when the service should be destroyed.
     */
    protected readonly destroy$ = new Subject<void>();

    /**
     * Public observable that emits when the service is being destroyed.
     * Used by ConfiguratorServiceRegistry to auto-unregister.
     */
    readonly onDestroy$ = this.destroy$.asObservable();

    /**
     * Subject that emits when the instance changes.
     * Services should use this to reset their streams.
     */
    protected readonly instanceReset$ = new Subject<void>();

    /**
     * The current instance ID
     */
    protected currentInstanceId: string | null = null;

    /**
     * Whether the service is in instance-scoped mode
     */
    protected readonly isInstanceScoped: boolean;

    /**
     * Internal subscriptions managed by the service
     */
    private subscriptions: Subscription[] = [];

    /**
     * Counter for instance resets - used to break distinctUntilChanged chains
     */
    private instanceSequence$ = new BehaviorSubject<number>(0);

    /**
     * Track if instance has been destroyed to prevent duplicate cleanup
     */
    private instanceDestroyed = false;

    /**
     * Check if the service has been destroyed.
     * Use this in subscription callbacks to prevent processing after teardown.
     */
    protected get isDestroyed(): boolean {
        return this.instanceDestroyed;
    }

    constructor(
        @Optional() @Inject(CONFIGURATOR_CONTEXT) protected readonly context: ConfiguratorContext | null
    ) {
        this.isInstanceScoped = !!context;
        this.currentInstanceId = context?.instanceId ?? null;
    }

    ngOnDestroy(): void {
        // Ensure cleanup runs (may already have been called by registry)
        this.teardown();

        // Complete subjects (safe to call multiple times)
        this.destroy$.complete();
        this.instanceReset$.complete();
    }

    /**
     * Public method to synchronously tear down the service.
     * Called by ConfiguratorServiceRegistry before moduleRef is destroyed.
     * Idempotent — safe to call multiple times.
     *
     * Ordering is critical:
     * 1. Set instanceDestroyed flag — guards all operators and callbacks
     * 2. Signal destroy$ — immediately terminates takeUntil(this.destroy$) streams
     * 3. Clear managed subscriptions — unsubscribes remaining subscriptions
     * 4. Notify subclass via onInstanceDestroy() — safe, all subscriptions are dead
     *
     * This ensures no subscription callback can fire after teardown begins,
     * even if onInstanceDestroy() triggers cascading state changes.
     */
    public teardown(): void {
        if (this.instanceDestroyed) {
            return;
        }
        this.instanceDestroyed = true;

        // 1. Signal destroy$ FIRST to immediately terminate all
        //    takeUntil(this.destroy$) streams (instanceAwareDistinct,
        //    takeUntilReset, untilDestroyed).
        //    This is an atomic operation that kills all guarded streams at once,
        //    preventing race conditions from sequential unsubscription.
        this.destroy$.next();

        // 2. Unsubscribe all managed subscriptions that were not using
        //    takeUntil(this.destroy$) (e.g. plain .subscribe() calls
        //    tracked via addSubscription).
        this.clearSubscriptions();

        // 3. Notify subclass AFTER all subscriptions are dead.
        //    Even if onInstanceDestroy() dispatches NgRx actions or triggers
        //    side-effects, no subscription callback in this service can fire.
        this.onInstanceDestroy();
    }

    /**
     * Add one or more subscriptions to be managed by the service.
     * These subscriptions will be automatically cleaned up on destroy.
     *
     * If the service has no instance context (legacy mode), subscriptions
     * are immediately unsubscribed to prevent crashes when the NgRx store
     * feature is removed by a different instance.
     *
     * If the service has already been destroyed, the subscriptions are
     * immediately unsubscribed to prevent leaked subscriptions.
     *
     * @example
     * this.addSubscription(
     *     this.facade.data$.subscribe(...),
     *     this.facade.config$.subscribe(...),
     *     this.facade.items$.subscribe(...)
     * );
     */
    protected addSubscription(...subscriptions: Subscription[]): void {
        // Skip subscriptions for legacy services without instance context.
        // These services should not maintain subscriptions to NgRx state
        // as they will crash when instance-scoped state is destroyed.
        if (!this.isInstanceScoped) {
            subscriptions.forEach(sub => sub.unsubscribe());
            return;
        }

        if (this.instanceDestroyed) {
            // Service already torn down — unsubscribe immediately
            subscriptions.forEach(sub => sub.unsubscribe());
            return;
        }
        this.subscriptions.push(...subscriptions);
    }

    /**
     * Clear all managed subscriptions
     */
    protected clearSubscriptions(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * Pipe operator that completes the stream when the service is destroyed
     * and filters out emissions arriving after the destroyed flag is set.
     *
     * Use this to protect any observable pipeline from post-teardown emissions,
     * especially for plain subscriptions that don't use instanceAwareDistinct.
     *
     * @example
     * this.addSubscription(
     *     this.facade.data$.pipe(
     *         this.untilDestroyed()
     *     ).subscribe(data => {
     *         // Guaranteed to never fire after teardown
     *         this.processData(data);
     *     })
     * );
     */
    protected untilDestroyed<T>(): MonoTypeOperatorFunction<T> {
        return (source: Observable<T>) => source.pipe(
            takeUntil(this.destroy$),
            filter(() => !this.instanceDestroyed)
        );
    }

    /**
     * Create an instance-aware distinctUntilChanged operator.
     *
     * This operator resets its comparison state when the instance changes,
     * ensuring that the first value after an instance change always emits
     * even if it's the same as the last value from the previous instance.
     *
     * Emissions are silently dropped after the service has been torn down,
     * preventing comparator errors from undefined/null values during cleanup.
     *
     * @param comparator Custom comparison function (optional)
     */
    protected instanceAwareDistinct<T>(
        comparator?: (previous: T, current: T) => boolean
    ): (source: Observable<T>) => Observable<T> {
        return (source: Observable<T>) => {
            // Use instanceSequence to track resets
            return new Observable<T>(subscriber => {
                let lastValue: T | undefined;
                let hasValue = false;
                let lastSequence = -1;

                const sequenceSub = this.instanceSequence$.subscribe(sequence => {
                    if (sequence !== lastSequence) {
                        lastSequence = sequence;
                        hasValue = false; // Reset comparison on instance change
                    }
                });

                const sourceSub = source.subscribe({
                    next: (value) => {
                        // Guard: drop emissions after service teardown.
                        // This prevents comparator errors from undefined/null
                        // values emitted by NgRx selectors during state cleanup
                        // or activeInstanceId changes.
                        if (this.instanceDestroyed) {
                            return;
                        }

                        let shouldEmit: boolean;
                        if (!hasValue) {
                            shouldEmit = true;
                        } else if (comparator) {
                            try {
                                shouldEmit = !comparator(lastValue as T, value);
                            } catch {
                                // Comparator threw (e.g. calling .map() on undefined).
                                // Treat as changed — emit the value and let the
                                // subscriber decide how to handle it.
                                shouldEmit = true;
                            }
                        } else {
                            shouldEmit = lastValue !== value;
                        }

                        if (shouldEmit) {
                            hasValue = true;
                            lastValue = value;
                            subscriber.next(value);
                        }
                    },
                    error: (err) => subscriber.error(err),
                    complete: () => subscriber.complete()
                });

                return () => {
                    sequenceSub.unsubscribe();
                    sourceSub.unsubscribe();
                };
            }).pipe(takeUntil(this.destroy$));
        };
    }

    /**
     * Pipe operator that takes until instance reset or service destroy.
     * Use this for subscriptions that should restart on instance change.
     */
    protected takeUntilReset<T>(): (source: Observable<T>) => Observable<T> {
        return (source: Observable<T>) => {
            return new Observable<T>(subscriber => {
                let innerSub: Subscription | null = null;

                const subscribe = () => {
                    innerSub?.unsubscribe();
                    innerSub = source.subscribe({
                        next: (v) => {
                            if (!this.instanceDestroyed) {
                                subscriber.next(v);
                            }
                        },
                        error: (e) => subscriber.error(e),
                        // Don't complete on instance reset
                    });
                };

                subscribe();

                const resetSub = this.instanceReset$.pipe(
                    skip(1) // Skip initial value
                ).subscribe(() => {
                    if (!this.instanceDestroyed) {
                        // Resubscribe to get fresh stream
                        subscribe();
                    }
                });

                return () => {
                    resetSub.unsubscribe();
                    innerSub?.unsubscribe();
                };
            }).pipe(takeUntil(this.destroy$));
        };
    }

    /**
     * Reset internal state.
     * Override in subclasses to reset service-specific state.
     */
    protected abstract resetState(): void;

    /**
     * Initialize subscriptions.
     * Override in subclasses to set up facade/store subscriptions.
     * Called in constructor and again when instance is reset.
     */
    protected initSubscriptions(): void {
        // Default: no-op — subclasses override to add subscriptions
    }

    /**
     * Called when a new instance is initialized.
     * Override in subclasses to perform initialization logic.
     */
    protected onInstanceInit(): void {
        // Default: no-op
    }

    /**
     * Called when the current instance is being destroyed.
     * Override in subclasses to perform cleanup logic.
     * Called AFTER all subscriptions have been unsubscribed,
     * so dispatching actions or triggering side-effects is safe.
     */
    protected onInstanceDestroy(): void {
        // Default: no-op
    }

    /**
     * Get the current instance ID
     */
    getInstanceId(): string | null {
        return this.currentInstanceId;
    }
}
